'use client';

import { revalidatePage } from '@/actions/server.actions';
import FollowUserButton from '@/components/Buttons/FollowUserButton';
import Avatar from '@/components/next-ui/Avatar';
import Card from '@/components/next-ui/Card';
import AppLink from '@/components/next-ui/Link/Link';
import Typography from '@/components/next-ui/Typography';
import useUserProfile from '@/hooks/useUserProfile';
import getQualifiedImageUrl from '@/utils/imageUrlProcessor';
import { formatNumberForLocale } from '@/utils/stringHelpers';
import { getJobTitle } from '@/utils/user.helper';
import { cx } from 'cva';
import { last, take } from 'lodash-es';
import Image from 'next/image';
import React from 'react';

const zIndeces = ['z-60', 'z-50', 'z-40', 'z-30', 'z-20', 'z-10'];

function ContributorCard(props) {
  const { user } = props;
  const { badges, badgesCount } = user;
  const badgesToRender = take(badges, 3);
  const currentUser = useUserProfile();
  const isCurrentUser = user.id === currentUser.id;
  const formattedPoints = formatNumberForLocale(user.points);

  return (
    <Card edgeSize="lg" padding="none" className="overflow-hidden">
      <div className="flex flex-col gap-6 p-4 lg:p-6">
        {/* User Info */}
        <div className="flex items-start gap-3">
          <Avatar
            src={user.avatar}
            alt={user.first_name}
            className="h-14 w-14"
          />

          <div className="flex flex-1 flex-col items-start gap-0.5">
            <AppLink href={user.slug ? `/${user.slug}` : undefined}>
              <Typography
                variant="h3"
                className="line-clamp-1 break-words leading-5 hover:text-text-hyperlink">
                {user.first_name} {user.last_name}
              </Typography>
            </AppLink>

            <Typography
              variant="body2"
              color="textSecondary"
              className="line-clamp-1">
              {getJobTitle(user)}
            </Typography>
          </div>
        </div>

        {/* Rewards Section (Nekies + Badges in two rows) */}
        <div className="ml-5 flex w-full flex-col gap-3">
          {/* Nekies Row */}
          <div className="flex items-center gap-2">
            <Image
              // src="/assets/images/neki-heart.png"
              src="/assets/icons/nekies.svg"
              width={28}
              height={28}
              alt="neki-score"
              className="h-7 w-7 object-contain"
            />
            <Typography variant="body2" className="font-medium">
              {/* Neki Score: {formattedPoints} */}
              {formattedPoints} Nekies
            </Typography>
          </div>

          {/* Badges Row */}
          <div className="flex items-center gap-2">
            <div className="flex items-center -space-x-2">
              {badgesToRender.map((badge, index) => (
                <div
                  key={badge.id}
                  className={cx(
                    'relative inline',
                    zIndeces[index] || last(zIndeces),
                  )}>
                  <Image
                    src={getQualifiedImageUrl(badge.imageUrl) || ''}
                    alt={badge.name}
                    width={28}
                    height={28}
                    className="h-7 w-7 rounded-full border border-white object-contain"
                  />
                </div>
              ))}

              {badgesCount > badgesToRender.length && (
                <div className="relative z-70 inline h-7 w-7">
                  <div className="flex h-full w-full items-center justify-center rounded-full border border-gray-300 bg-gray-100">
                    <Typography variant="caption" color="secondary">
                      +{badgesCount - badgesToRender.length}
                    </Typography>
                  </div>
                </div>
              )}

              {badgesCount === 0 && (
                <div className="relative z-70 inline h-7 w-7">
                  <div className="flex h-full w-full items-center justify-center rounded-full border border-gray-200 bg-body-background">
                    <Typography variant="caption" color="secondary">
                      0
                    </Typography>
                  </div>
                </div>
              )}
            </div>

            <Typography variant="body2" className="font-medium">
              {badgesCount > 0 ? `${badgesCount} Badges` : 'Badges'}
            </Typography>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="h-0.5 w-full bg-body-background" />

      {/* Follow Button */}
      <div
        className={cx(
          'flex w-full items-center justify-center',
          isCurrentUser && 'h-10',
        )}>
        {!isCurrentUser && (
          <FollowUserButton
            user={user}
            viewer={currentUser}
            variant="text"
            color="primary"
            noConfirm
            revalidate={() => revalidatePage('/home')}
          />
        )}
      </div>
    </Card>
  );
}

export default React.memo(ContributorCard);
